import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgModel } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { OrganizationRiderDetail, Rider } from '@uzurv/clara';
import { isNullOrUndefined, PermissionsConstants, PhonePipe } from '@uzurv/core';
import { AuthDataService } from '@uzurv/http';

interface RiderContactProfile {
  phoneNumber: string;
  organizationCode: string;
}

@Component({
  selector: 'app-rider-phone-number',
  templateUrl: './rider-phone-number.component.html',
  styleUrls: ['./rider-phone-number.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class RiderPhoneNumberComponent implements OnInit, OnChanges {
  @Input() new = false;
  @Input() organizationRiderProfile: OrganizationRiderDetail;
  @Input() uzurvRiderProfile: Rider;
  @Input() required = false;

  @Output() selectedPhoneNumber = new EventEmitter<string>();

  @ViewChild('phoneNumber') phoneNumberInput: NgModel;

  riderContactProfiles: Array<RiderContactProfile> = [];
  currentPhoneNumberSelection;

  readonly uzurvOrganizationCode = 'UZURV';

  constructor(private authDataService: AuthDataService, private phonePipe: PhonePipe) {}

  /**
   * Populate the dropdown with the rider's phone information.
   */
  ngOnInit() {
    if (!isNullOrUndefined(this.organizationRiderProfile?.phoneNumber) || !isNullOrUndefined(this.uzurvRiderProfile?.phoneNumber)) {
      this.populateRiderPhoneNumbers();
    }
  }

  /**
   * If the rider is changed, update the phone numbers.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['organizationRiderProfile'] || changes['uzurvRiderProfile']) {
      this.populateRiderPhoneNumbers();
    }
  }

  /**
   * Format the phone numbers and append the number's source.
   */
  formatPhoneNumber(phoneNumber: string, organizationCode: string): string {
    return `${this.phonePipe.transform(phoneNumber)} (${organizationCode})`;
  }

  /**
   * If the phone number changes, emit that value.
   */
  handlePhoneNumberSelected(phoneNumber: MatSelectChange): void {
    this.selectedPhoneNumber.emit(phoneNumber.value);
  }

  /**
   * Populate the rider phone number options.
   */
  private populateRiderPhoneNumbers() {
    // Clear the array to avoid any duplicated entries.
    this.riderContactProfiles = [];

    // If the user can see the person record, add that phone number.
    if (this.authDataService.hasPermission({ any: [], all: [PermissionsConstants.RIDER_READ] })) {
      this.riderContactProfiles.push({ phoneNumber: this.uzurvRiderProfile.phoneNumber, organizationCode: this.uzurvOrganizationCode });
    }

    // If the user can see the organization rider record, add that phone number.
    if (this.authDataService.hasPermission({ any: [], all: [PermissionsConstants.ORGANIZATION_RIDER_READ] })) {
      this.riderContactProfiles.push({
        phoneNumber: this.organizationRiderProfile.phoneNumber,
        organizationCode: this.organizationRiderProfile.organizationCode
      });
    }

    // Remove any duplicate organization phone number in favor of the person record phone number.
    this.riderContactProfiles = this.riderContactProfiles.reduce((accumulator, currentProfile) => {
      if (accumulator.findIndex(contactProfile => contactProfile.phoneNumber === currentProfile.phoneNumber) === -1) {
        accumulator.push(currentProfile);
      }

      return accumulator;
    }, []);

    // Set the dropdown's default value to the first phone number entry.
    this.currentPhoneNumberSelection = this.riderContactProfiles[0].phoneNumber;

    // Emit that entry as the current selection.
    this.selectedPhoneNumber.emit(this.currentPhoneNumberSelection);
  }
}
